import { useState, useCallback, useRef, useEffect } from 'react';
import { useSigma, useCamera } from '@react-sigma/core';
import { useGraphStore } from '@/stores/graph';
import { Search, X, Loader2 } from 'lucide-react';
import { clsx } from 'clsx';
import { ENTITY_CLASS_COLORS } from '@/services/api';
import type { NodeAttributes, EdgeAttributes } from './graphAdapter';

export function GraphSearchBar() {
  const sigma = useSigma<NodeAttributes, EdgeAttributes>();
  const { gotoNode } = useCamera();
  const {
    searchQuery,
    searchResults,
    loading,
    search,
    clearSearch,
    selectNode,
    setHighlightedNodes,
  } = useGraphStore();

  const [localQuery, setLocalQuery] = useState(searchQuery);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setDropdownOpen(searchResults.length > 0);
  }, [searchResults]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (localQuery.trim()) {
        search(localQuery);
      }
    },
    [localQuery, search],
  );

  const handleClear = useCallback(() => {
    setLocalQuery('');
    clearSearch();
    setDropdownOpen(false);
  }, [clearSearch]);

  const handleResultClick = useCallback(
    (resultId: string) => {
      const graph = sigma.getGraph();
      if (graph.hasNode(resultId)) {
        selectNode(resultId);
        gotoNode(resultId, { duration: 500 });
        setHighlightedNodes(new Set([resultId]));
      } else {
        selectNode(null);
        setHighlightedNodes(new Set([resultId]));
      }
      setDropdownOpen(false);
    },
    [sigma, selectNode, gotoNode, setHighlightedNodes],
  );

  return (
    <div ref={containerRef} className="absolute top-3 left-3 right-3 z-20 max-w-lg">
      <form onSubmit={handleSubmit}>
        <div className="relative">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            ref={inputRef}
            type="text"
            value={localQuery}
            onChange={(e) => setLocalQuery(e.target.value)}
            placeholder="Search the cognitive graph..."
            className="w-full pl-8 pr-20 py-2 bg-slate-900/90 backdrop-blur border border-slate-700/60 rounded-lg text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-teal-500/50 focus:border-teal-500/50"
          />
          <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {localQuery && (
              <button
                type="button"
                onClick={handleClear}
                className="p-1 text-slate-500 hover:text-slate-300 transition-colors"
              >
                <X size={12} />
              </button>
            )}
            <button
              type="submit"
              disabled={loading || !localQuery.trim()}
              className="px-2 py-1 bg-teal-600/80 hover:bg-teal-500 disabled:bg-slate-700 disabled:text-slate-500 text-white text-[10px] font-medium rounded transition-colors"
            >
              {loading ? <Loader2 size={10} className="animate-spin" /> : 'Search'}
            </button>
          </div>
        </div>
      </form>

      {dropdownOpen && searchResults.length > 0 && (
        <div className="mt-1.5 bg-slate-900/95 backdrop-blur border border-slate-700/60 rounded-lg shadow-xl max-h-72 overflow-y-auto">
          <div className="px-3 py-1.5 border-b border-slate-800">
            <span className="text-[10px] text-slate-500">
              {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
            </span>
          </div>
          {searchResults.map((r) => {
            const inGraph = sigma.getGraph().hasNode(r.id);
            const color = ENTITY_CLASS_COLORS[r.type] ?? '#94a3b8';
            return (
              <button
                key={r.id}
                onClick={() => handleResultClick(r.id)}
                className={clsx(
                  'w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors',
                  'hover:bg-slate-800/80',
                  !inGraph && 'opacity-50',
                )}
              >
                <span
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: color }}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-white truncate">{r.name}</p>
                  {r.summary && (
                    <p className="text-[10px] text-slate-500 truncate">{r.summary}</p>
                  )}
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <span className="text-[9px] font-mono text-slate-500">
                    {(r.score * 100).toFixed(0)}%
                  </span>
                  <span className="text-[9px] uppercase font-bold text-slate-500 bg-slate-800 px-1 py-0.5 rounded">
                    {r.type}
                  </span>
                  {!inGraph && (
                    <span className="text-[9px] text-amber-400/60">not in view</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
